import { useEffect, useMemo } from 'react'
import type { LeaderGroupNode } from '@/modules/companies'

type UpdateFormOptions = {
  runCallbacks?: boolean
  runValidation?: boolean
}

interface Props {
  canViewOthers: boolean
  isAdmin: boolean
  leaderGroup: string | null | undefined
  leaderGroups: LeaderGroupNode[]
  loadStoredFormState: (skipTransforms?: boolean) => unknown | null
  updateForm: (
    values: Record<string, unknown>,
    options?: UpdateFormOptions
  ) => void
  onAutoSelect: (group: LeaderGroupNode) => void
}

export function useAutoSelectSingleLeaderGroup({
  canViewOthers,
  isAdmin,
  leaderGroup,
  leaderGroups,
  loadStoredFormState,
  updateForm,
  onAutoSelect,
}: Props) {
  const hasStoredState = useMemo(
    // `false` means "do not skip transforms" in @ur-solutions/react-hooks.
    // We only need to know whether any stored payload exists.
    () => loadStoredFormState(false) !== null,
    [loadStoredFormState]
  )

  useEffect(() => {
    if (!hasStoredState) {
      updateForm({}, { runCallbacks: false, runValidation: false })
    }
  }, [hasStoredState, updateForm])

  // Tri-state semantics:
  // undefined: no stored field (eligible for single-group auto-select)
  // null: explicit "no group selected" from storage/user
  // string: selected leader group id
  const effectiveLeaderGroup =
    hasStoredState && leaderGroup === undefined ? null : leaderGroup

  useEffect(() => {
    if (effectiveLeaderGroup !== undefined) return

    const onlyLeaderGroup = leaderGroups.length === 1 ? leaderGroups[0] : null

    if (!canViewOthers || !onlyLeaderGroup || isAdmin) return

    onAutoSelect(onlyLeaderGroup)
  }, [
    canViewOthers,
    effectiveLeaderGroup,
    isAdmin,
    leaderGroups,
    onAutoSelect,
  ])
}
